/**
 * *Starred*: the account's starred filters, and the one window they would be applied to
 * (LPN-68).
 *
 * Lunarbor's Starred dialog, carried over nearly as-is — the same header (title, *Add to
 * starred*, ×), the same palette-style list, the same keys: ↑/↓ (and Home/End) move the
 * highlight, Enter picks, Esc closes, and ⌘S pressed again while it is open stars or
 * un-stars the window's current filter (the host's ⌘S listener routes that press here —
 * see `toggleCurrentFromShortcut`). Picking a row **replaces the window's filter**; it
 * does not open a new window.
 *
 * Opened for one window, whose live filter it reads through [currentFilter] each time it
 * needs it — the stored one can lag by a debounce, and "is this starred?" has to be asked of
 * what the window shows now.
 *
 * Wears the toolkit's `.dt-modal` / `.dt-modal-backdrop`, as `TidyTagsDialog` does, and
 * builds everything with `textContent`: a label and a summary are made of tags and search
 * text somebody typed.
 *
 * @param model the account's starred filters. Every change goes to it.
 * @param currentFilter the window's filter as it stands, or null once the window is gone.
 * @param windowName the window's own name, if it has one — becomes a new star's label.
 * @param onPick a row was picked; the host applies the filter to the window.
 * @param onClosed the dialog has left the document, by any route.
 */
package se.soderbjorn.lunapin.web

import kotlinx.browser.document
import org.w3c.dom.HTMLElement
import org.w3c.dom.events.Event
import org.w3c.dom.events.KeyboardEvent
import org.w3c.dom.events.MouseEvent
import se.soderbjorn.lunapin.client.viewmodel.StarredFiltersBackingViewModel
import se.soderbjorn.lunapin.client.viewmodel.WindowContent

internal class StarredFiltersDialog(
    private val model: StarredFiltersBackingViewModel,
    private val currentFilter: () -> WindowContent.BookmarkList?,
    private val windowName: () -> String?,
    private val onPick: (WindowContent.BookmarkList) -> Unit,
    private val onClosed: () -> Unit,
) {
    private val backdrop = element("div", "dt-modal-backdrop lp-starred-backdrop")
    private val card = element("div", "dt-modal lp-starred")
    private val list = element("div", "lp-starred-list")
    private val addIcon = element("span", "lp-starred-add-icon")
    private val addLabel = element("span", "lp-starred-add-label")
    private val addButton = button("", "lp-starred-add") { toggleCurrent() }

    private var rows: List<StarredFiltersBackingViewModel.Row> = emptyList()
    private var highlighted = 0
    private var keyHandler: ((Event) -> Unit)? = null
    private val opener: HTMLElement? = document.activeElement as? HTMLElement

    /** Put it in the document and paint [state]. */
    fun mount(state: StarredFiltersBackingViewModel.State) {
        card.setAttribute("role", "dialog")
        card.setAttribute("aria-modal", "true")
        card.setAttribute("aria-label", "Starred")

        val header = element("div", "lp-starred-header")
        header.appendChild(element("div", "lp-starred-title", "Starred"))
        addButton.tabIndex = -1
        addButton.appendChild(addIcon)
        addButton.appendChild(addLabel)
        header.appendChild(addButton)
        val close = button("×", "lp-starred-close") { dispose() }
        close.title = "Close"
        close.setAttribute("aria-label", "Close")
        close.tabIndex = -1
        header.appendChild(close)
        card.appendChild(header)
        card.appendChild(list)

        backdrop.appendChild(card)
        backdrop.addEventListener("mousedown", { ev ->
            if (ev.target === backdrop) {
                ev.preventDefault()
                dispose()
            }
        })
        document.body?.appendChild(backdrop)

        // Capture phase, so ↑/↓/Enter reach this before the list window's own row keys.
        val handler: (Event) -> Unit = { ev -> onKeyDown(ev as KeyboardEvent) }
        document.addEventListener("keydown", handler, true)
        keyHandler = handler

        render(state)
    }

    /** Whether it is still in the document. */
    val isOpen: Boolean get() = keyHandler != null

    /** ⌘S while open — the host's shortcut listener calls this instead of reopening. */
    fun toggleCurrentFromShortcut() = toggleCurrent()

    /** Take it out of the document and leave nothing behind. Idempotent. */
    fun dispose() {
        val handler = keyHandler ?: return
        document.removeEventListener("keydown", handler, true)
        keyHandler = null
        backdrop.parentNode?.removeChild(backdrop)
        opener?.takeIf { document.contains(it) }?.focus()
        onClosed()
    }

    /** Paint [state]: the rows, and whether the window's filter is among them. */
    fun render(state: StarredFiltersBackingViewModel.State) {
        if (!isOpen) return
        val priorFilter = rows.getOrNull(highlighted)?.filter
        rows = state.rows
        highlighted = rows.indexOfFirst { it.filter == priorFilter }.takeIf { it >= 0 } ?: 0

        val filter = currentFilter()
        val starred = filter != null && model.isStarred(filter)
        addIcon.innerHTML = if (starred) STAR_FILLED_ICON else STAR_ICON
        addLabel.textContent = if (starred) "Remove from starred" else "Add to starred"
        addButton.classList.toggle("is-active", starred)
        addButton.title = (if (starred) "Remove this window's filter from" else "Add this window's filter to") +
            " your starred filters (⌘S)"

        while (list.firstChild != null) list.removeChild(list.firstChild!!)
        if (rows.isEmpty()) {
            list.appendChild(
                element(
                    "div",
                    "lp-starred-empty",
                    "No starred filters yet. Press ⌘S again (or Add to starred) to star this window's filter.",
                ),
            )
            return
        }
        rows.forEachIndexed { index, row ->
            val item = element("div", "lp-starred-item")
            item.setAttribute("role", "button")
            val text = element("div", "lp-starred-item-text")
            text.appendChild(element("div", "lp-starred-item-label", row.label))
            if (row.summary != row.label) {
                text.appendChild(element("div", "lp-starred-item-summary", row.summary))
            }
            item.appendChild(text)
            val remove = button("×", "lp-starred-item-remove") { model.onStarRemoved(row.filter) }
            remove.title = "Remove from starred"
            remove.setAttribute("aria-label", "Remove ${row.label} from starred")
            remove.tabIndex = -1
            remove.addEventListener("click", { ev -> ev.stopPropagation() })
            item.appendChild(remove)

            item.addEventListener("mousemove", { _ ->
                if (highlighted != index) {
                    highlighted = index
                    paintHighlight()
                }
            })
            item.addEventListener("mousedown", { ev -> (ev as MouseEvent).preventDefault() })
            item.addEventListener("click", { _ ->
                highlighted = index
                pickHighlighted()
            })
            list.appendChild(item)
        }
        paintHighlight()
    }

    private fun paintHighlight() {
        val items = list.children
        for (i in 0 until items.length) {
            val item = items.item(i) as? HTMLElement ?: continue
            item.classList.toggle("is-active", i == highlighted)
            if (i == highlighted) item.scrollIntoView(js("({block:'nearest'})"))
        }
    }

    private fun pickHighlighted() {
        val row = rows.getOrNull(highlighted) ?: return
        onPick(row.filter)
        dispose()
    }

    private fun toggleCurrent() {
        val filter = currentFilter() ?: return
        model.onStarToggled(filter, windowName())
    }

    private fun onKeyDown(event: KeyboardEvent) {
        // ⌘S is not handled here: the host's capture listener sees it first and calls
        // [toggleCurrentFromShortcut]. Handling it here too would toggle twice.
        val move: Int? = when (event.key) {
            "ArrowDown" -> highlighted + 1
            "ArrowUp" -> highlighted - 1
            "Home" -> 0
            "End" -> rows.lastIndex
            else -> null
        }
        when {
            event.key == "Escape" -> {
                event.preventDefault()
                event.stopPropagation()
                dispose()
            }
            event.key == "Enter" && rows.isNotEmpty() -> {
                event.preventDefault()
                event.stopPropagation()
                pickHighlighted()
            }
            move != null && rows.isNotEmpty() -> {
                event.preventDefault()
                event.stopPropagation()
                highlighted = move.coerceIn(0, rows.lastIndex)
                paintHighlight()
            }
        }
    }
}

/** Five-point outline star — the list window's toolbar button, and *Add to starred*. */
internal const val STAR_ICON: String =
    "<svg viewBox=\"0 0 24 24\" width=\"14\" height=\"14\" fill=\"none\" " +
        "stroke=\"currentColor\" stroke-width=\"2\" stroke-linecap=\"round\" " +
        "stroke-linejoin=\"round\" aria-hidden=\"true\">" +
        "<polygon points=\"12 2 15 9 22 9.5 17 14.5 18.5 21.5 12 18 5.5 21.5 7 14.5 2 9.5 9 9\"/>" +
        "</svg>"

/** The filled star — shown when the window's filter is starred. */
internal const val STAR_FILLED_ICON: String =
    "<svg viewBox=\"0 0 24 24\" width=\"14\" height=\"14\" fill=\"currentColor\" " +
        "stroke=\"currentColor\" stroke-width=\"2\" stroke-linecap=\"round\" " +
        "stroke-linejoin=\"round\" aria-hidden=\"true\">" +
        "<polygon points=\"12 2 15 9 22 9.5 17 14.5 18.5 21.5 12 18 5.5 21.5 7 14.5 2 9.5 9 9\"/>" +
        "</svg>"
